/**
 * Link-native economic reading of a [LocalSocialView], projected to plain numbers a front-end can render (roadmap
 * Milestone 6).
 *
 * The engine ([com.oasisfeng.invaluable.valuation.impl.SettledFlow]) values the view's
 * [com.oasisfeng.invaluable.actor.CommitmentSnapshot] into an anchor-free [ValueField].
 * Two kinds of number come out of it, and the distinction is load-bearing:
 *
 *  - **Frame-free** — every actor's gross `issuance` and every link's conserved ownership `share`. These need no
 *    measuring frame, so they are *always* available, even before this node has staked anything.
 *  - **Relative** — currency prices, assets, and a link's net value. These are meaningful only once a measuring frame is
 *    chosen. This node measures in **its own currency** (`priceOf(localActor) == 1`), the frame that makes "value is
 *    relative" concrete and keeps human and agent nodes symmetric. The frame is absent (`priced` = false) when the local
 *    actor has not yet committed anything (so it carries no price) or its price is degenerate.
 *
 * Pricing is a single conserved attribution, so it is computed once per board build ([FieldReading.from]) and queried,
 * never recomputed per card. Failures degrade to "unpriced" rather than throwing — a board must always render.
 */

package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.valuation.AnchoredValuation
import com.oasisfeng.invaluable.valuation.LinkOwnership
import com.oasisfeng.invaluable.valuation.ValueField
import com.oasisfeng.invaluable.valuation.impl.SettledFlow
import kotlin.math.abs
import kotlin.math.round

/**
 * The economic reading attached to a [SocialBoard], keyed by content-addressed [SocialRef] so every front-end (Web,
 * TUI, agent tools) names the same post, route or actor the same way it does for everything else. Routes are valued
 * like posts on purpose: `recommend` mints an ownable, appreciating claim, so leaving it out of this projection would
 * hide an actor's own attention holdings from them.
 *
 * [priced] reports whether a self-anchored frame exists; the relative lookups ([valueOf]/[priceOf]/[assetsOf]) return
 * null when it does not, while the frame-free ones ([shareOf]/[residualOf]/[issuanceOf]) are always meaningful.
 */
class BoardValuation internal constructor(
    val priced: Boolean,
    private val linkValue: Map<String, Double>,
    private val linkShare: Map<String, Double>,
    private val linkResidual: Map<String, Double>,
    private val actorPrice: Map<String, Double>,
    private val actorAssets: Map<String, Double>,
    private val actorIssuance: Map<String, Double>,
) {
    /** Net value of a post's or route's subtree in this node's own currency; null when no frame is available. */
    fun valueOf(ref: SocialRef): Double? = linkValue[ref.value]

    /** The local actor's conserved (frame-free) ownership fraction of a post's or route's value; 0 when none. */
    fun shareOf(ref: SocialRef): Double = linkShare[ref.value] ?: 0.0

    /** Frame-free fraction of a post's or route's value attributed to ancestors above it; null when not valued. */
    fun residualOf(ref: SocialRef): Double? = linkResidual[ref.value]

    /** Relative currency price of an actor in this node's frame (the local actor reads 1); null when no frame. */
    fun priceOf(ref: SocialRef): Double? = actorPrice[ref.value]

    /** Market value of an actor's currency in this node's frame (price × issuance); null when no frame. */
    fun assetsOf(ref: SocialRef): Double? = actorAssets[ref.value]

    /** Frame-free gross issuance of an actor (sum of |committed quantities|); null when the actor is unknown here. */
    fun issuanceOf(ref: SocialRef): Double? = actorIssuance[ref.value]

    companion object {
        val EMPTY = BoardValuation(false, emptyMap(), emptyMap(), emptyMap(), emptyMap(), emptyMap(), emptyMap())
    }
}

/**
 * A solved [ValueField] paired with the local actor's measuring frame, offering null-safe queries by model object.
 *
 * Built once from a view; [SocialBoard.from] queries it per post/actor and flattens the answers into [SocialRef]-keyed
 * maps. Kept internal so [ValueField]/[Actor]/[NestedLink] never leak through the board's plain-data surface.
 */
internal class FieldReading private constructor(
    private val localActor: Actor,
    private val field: ValueField,
    private val frame: AnchoredValuation?,
) {
    /** Whether a self-anchored measuring frame exists; relative numbers are null without it. */
    val priced: Boolean get() = frame != null

    fun value(link: NestedLink): Double? = frame?.valueOf(link)
    fun share(link: NestedLink): Double = field.ownership(link).shareOf(localActor)
    fun shares(link: NestedLink): List<Pair<Actor, Double>> {
        val ownership = field.ownership(link)
        return ownership.claimants
            .mapNotNull { actor ->
                val share = ownership.shareOf(actor)
                if (share > 0.0) actor to share else null
            }
            .sortedWith(compareByDescending<Pair<Actor, Double>> { it.first == localActor }.thenByDescending { it.second })
    }
    fun residual(link: NestedLink): Double = field.ownership(link).residual
    fun price(actor: Actor): Double? = frame?.priceOf(actor)
    fun assets(actor: Actor): Double? = frame?.assetsOf(actor)
    fun issuance(actor: Actor): Double = field.issuance(actor).toDouble()

    companion object {
        fun from(view: LocalSocialView): FieldReading {
            // Valuation over withheld/degenerate material can fail; a board must still render, so fall back to an empty
            // field (no actors, no value) rather than throw.
            val field = runCatching { SettledFlow.valuate(view.snapshot) }.getOrNull() ?: EmptyField
            val frame =
                if (view.localActor in field.actors) runCatching { field.relativeTo(view.localActor) }.getOrNull()
                else null
            return FieldReading(view.localActor, field, frame)
        }

        /** Stand-in field when valuation cannot be computed: everyone unknown, nothing valued. */
        private val EmptyField = object : ValueField {
            override val actors: Set<Actor> = emptySet()
            override fun issuance(actor: Actor): Quantity = Quantity.ZERO
            override fun ownership(link: NestedLink): LinkOwnership = object : LinkOwnership {
                override val link: NestedLink = link
                override val claimants: Set<Actor> = emptySet()
                override fun shareOf(actor: Actor): Double = 0.0
                override val residual: Double = 0.0
            }
            override fun relativeTo(anchor: Actor): AnchoredValuation =
                throw IllegalArgumentException("empty field has no frame")
        }
    }
}

/** Compact decimal for prompts/UI: rounded to [decimals] places with trailing zeros trimmed (no `String.format` in common). */
fun formatAmount(value: Double, decimals: Int = 2): String {
    if (!value.isFinite()) return "0"
    val negative = value < 0
    var factor = 1L
    repeat(decimals) { factor *= 10 }
    val scaled = round(abs(value) * factor).toLong()
    if (scaled == 0L) return "0"
    val whole = scaled / factor
    val frac = (scaled % factor).toString().padStart(decimals, '0').trimEnd('0')
    return buildString {
        if (negative) append('-')
        append(whole)
        if (frac.isNotEmpty()) append('.').append(frac)
    }
}

/** Render an ownership/residual fraction (0..1) as a percentage string, e.g. `12.5%`. */
fun formatPercent(fraction: Double): String = "${formatAmount(fraction * 100, decimals = 1)}%"

/**
 * How a push suggestion's subject stands in the local actor's own account, given their [share] of it.
 *
 * Both readings are eligible pushes, and neither is stated as the better one: routing a branch already held adds to a
 * position, routing an unheld one opens one — paying for the route is itself a claim on the subject. Naming which is
 * which keeps the reader from mistaking "I hold none of it" for "this is not mine to push" (the filter this replaced).
 */
fun holdingNote(share: Double): String =
    if (share > 0.0) "you already hold ${formatPercent(share)} of this branch"
    else "you hold none of it yet; the route would open your position"
